/**
 * Office Onboarding Service
 *
 * This service handles all API calls for the office onboarding flow.
 * Uses backend API for step persistence across browsers.
 */

import {
  OfficeOnboardingStep1Data,
  OfficeOnboardingStep2Data,
  OfficeOnboardingStep3Data,
  OfficeOnboardingStep4Data,
} from "../redux/slices/OfficeOnboardingSlice";
import { OfficeService } from "./office.service";

export interface OnboardingStatus {
  currentStep: number;
  isCompleted: boolean;
  step1Data: OfficeOnboardingStep1Data | null;
  step2Data: OfficeOnboardingStep2Data | null;
  step3Data: OfficeOnboardingStep3Data | null;
  step4Data: OfficeOnboardingStep4Data | null;
}

class OfficeOnboardingService {
  /**
   * Get current onboarding status from API
   * Fetches step from /api/v2/offices/{officeId} and step data from localStorage
   */
  async getOnboardingStatus(officeId?: string): Promise<OnboardingStatus> {
    // Get step data from localStorage (for form data persistence)
    const stored = localStorage.getItem("officeOnboardingStatus");
    let stepData: OnboardingStatus | null = null;

    if (stored) {
      try {
        stepData = JSON.parse(stored);
      } catch (e) {
        // Invalid stored data, continue
      }
    }

    // Fetch current step from API if officeId is provided
    let currentStep = 1; // Default to step 1
    if (officeId) {
      try {
        const office = await OfficeService.getOfficeV2(officeId);
        if (office?.step !== undefined && office.step !== null) {
          currentStep = office.step;
        }
      } catch (error) {
        // If API call fails, fall back to localStorage or default
        if (stepData) {
          currentStep = stepData.currentStep;
        }
      }
    } else if (stepData) {
      // If no officeId, use localStorage step
      currentStep = stepData.currentStep;
    }

    // If currentStep is 5, onboarding modal steps are complete (Steps 1-5 in OnboardingModal)
    // Note: bankCompleted & billingCompleted are separate flags for bank info and deposit account details
    const isCompleted = currentStep === 5 || stepData?.isCompleted || false;

    // Return status with step from API and data from localStorage
    return {
      currentStep,
      isCompleted,
      step1Data: stepData?.step1Data || null,
      step2Data: stepData?.step2Data || null,
      step3Data: stepData?.step3Data || null,
      step4Data: stepData?.step4Data || null,
    };
  }

  /**
   * Save Step 1 data (Office Information)
   * Backend automatically updates step when step data is saved
   */
  async saveStep1(data: OfficeOnboardingStep1Data, officeId?: string): Promise<OfficeOnboardingStep1Data> {
    const status = await this.getOnboardingStatus(officeId);
    status.step1Data = data;
    status.currentStep = 2; // Move to next step

    // Save to localStorage for step data persistence
    // Note: Backend automatically updates the step when step data is saved
    localStorage.setItem("officeOnboardingStatus", JSON.stringify(status));

    return data;
  }

  /**
   * Save Step 2 data (Letterhead)
   * Backend automatically updates step when step data is saved
   */
  async saveStep2(data: OfficeOnboardingStep2Data, officeId?: string): Promise<OfficeOnboardingStep2Data> {
    const status = await this.getOnboardingStatus(officeId);
    status.step2Data = data;
    status.currentStep = 3;

    // Save to localStorage for step data persistence
    // Note: Backend automatically updates the step when step data is saved
    localStorage.setItem("officeOnboardingStatus", JSON.stringify(status));

    return data;
  }

  /**
   * Save Step 3 data (Primary Care Provider)
   * Backend automatically updates step when step data is saved
   */
  async saveStep3(data: OfficeOnboardingStep3Data, officeId?: string): Promise<OfficeOnboardingStep3Data> {
    const status = await this.getOnboardingStatus(officeId);
    status.step3Data = data;
    status.currentStep = 4;

    // Save to localStorage for step data persistence
    // Note: Backend automatically updates the step when step data is saved
    localStorage.setItem("officeOnboardingStatus", JSON.stringify(status));

    return data;
  }

  /**
   * Save Step 4 data (Service Pricing)
   * Backend automatically updates step when step data is saved
   */
  async saveStep4(data: OfficeOnboardingStep4Data, officeId?: string): Promise<OfficeOnboardingStep4Data> {
    const status = await this.getOnboardingStatus(officeId);
    status.step4Data = data;
    status.currentStep = 5;

    // Save to localStorage for step data persistence
    // Note: Backend automatically updates the step when step data is saved
    localStorage.setItem("officeOnboardingStatus", JSON.stringify(status));

    return data;
  }

  /**
   * Complete onboarding
   * Marks onboarding as completed (step remains at 5)
   */
  async completeOnboarding(officeId?: string): Promise<void> {
    const status = await this.getOnboardingStatus(officeId);
    status.isCompleted = true;
    status.currentStep = 5;

    // Note: We don't update step in API here since it's already at 5
    // Just mark as completed in localStorage
    localStorage.setItem("officeOnboardingStatus", JSON.stringify(status));
  }
}

export const officeOnboardingService = new OfficeOnboardingService();
